import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { PRODUCT_TYPES } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { PdfViewerModal } from '../components/PdfViewerModal';
import { 
  Search, Filter, FileText, Package, User, Calendar, Download, QrCode, History, 
  Loader2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Power, PowerOff, Merge, AlertTriangle, Eye, FileBox, Pencil, Check, X
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Catalogo() {
  const { canCreate, user } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [productType, setProductType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [selectedCode, setSelectedCode] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [history, setHistory] = useState([]);
  
  // Edit description state
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  
  // Edit unit state
  const [editingUnit, setEditingUnit] = useState(false);
  const [editedUnit, setEditedUnit] = useState('');
  const [savingUnit, setSavingUnit] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Deactivate/Activate state
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Merge state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [selectedMergeTarget, setSelectedMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const [searchingMerge, setSearchingMerge] = useState(false);

  // PDF Viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfViewerCode, setPdfViewerCode] = useState(null);

  useEffect(() => {
    fetchCodes();
  }, [productType, currentPage, pageSize, statusFilter]);

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      params.append('status', statusFilter);
      if (productType && productType !== 'all') {
        params.append('product_type', productType);
      }
      if (search) {
        params.append('search', search);
      }
      const response = await axios.get(`${API_URL}/api/codes?${params.toString()}`);
      setCodes(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch codes:', error);
      toast.error('Erro ao carregar códigos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    fetchCodes();
  };

  const handleProductTypeChange = (value) => {
    setProductType(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(parseInt(value));
    setCurrentPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (productType && productType !== 'all') params.append('product_type', productType);
      
      const response = await axios.get(`${API_URL}/api/codes/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `codigos_tecnoservice_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Arquivo exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar arquivo');
    } finally {
      setExporting(false);
    }
  };

  const fetchCodeHistory = async (codeId) => {
    try {
      const response = await axios.get(`${API_URL}/api/codes/${codeId}/history`);
      setHistory(response.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      setHistory([]);
    }
  };

  const handleSelectCode = async (code) => {
    setSelectedCode(code);
    setShowQRCode(false);
    await fetchCodeHistory(code.id);
  };

  // Deactivate/Activate handlers
  const handleDeactivate = async () => {
    if (!selectedCode) return;
    setDeactivating(true);
    try {
      const endpoint = selectedCode.status === 'inactive' 
        ? `/api/codes/${selectedCode.id}/activate`
        : `/api/codes/${selectedCode.id}/deactivate`;
      
      await axios.patch(`${API_URL}${endpoint}`);
      toast.success(selectedCode.status === 'inactive' 
        ? 'Código reativado com sucesso!' 
        : 'Código desativado com sucesso!');
      setShowDeactivateDialog(false);
      setSelectedCode(null);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status do código');
    } finally {
      setDeactivating(false);
    }
  };

  // Merge handlers
  const searchMergeCodes = async (query) => {
    if (!query || query.length < 2) {
      setMergeSearchResults([]);
      return;
    }
    setSearchingMerge(true);
    try {
      const response = await axios.get(`${API_URL}/api/codes?search=${encodeURIComponent(query)}&page_size=10&status=active`);
      // Filter out the current selected code
      const results = (response.data.items || []).filter(c => c.id !== selectedCode?.id);
      setMergeSearchResults(results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearchingMerge(false);
    }
  };

  const handleMerge = async () => {
    if (!selectedCode || !selectedMergeTarget) return;
    setMerging(true);
    try {
      // Determine which is newer based on created_at
      const sourceDate = new Date(selectedCode.created_at);
      const targetDate = new Date(selectedMergeTarget.created_at);
      
      // The newer code should be the target (kept active)
      const sourceId = sourceDate > targetDate ? selectedMergeTarget.id : selectedCode.id;
      const targetId = sourceDate > targetDate ? selectedCode.id : selectedMergeTarget.id;
      
      await axios.post(`${API_URL}/api/codes/merge`, {
        source_code_id: sourceId,
        target_code_id: targetId
      });
      
      const keptCode = sourceDate > targetDate ? selectedCode.code : selectedMergeTarget.code;
      toast.success(`Códigos mesclados! O código ${keptCode} foi mantido (mais recente).`);
      setShowMergeDialog(false);
      setSelectedMergeTarget(null);
      setMergeSearch('');
      setMergeSearchResults([]);
      setSelectedCode(null);
      fetchCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao mesclar códigos');
    } finally {
      setMerging(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!selectedCode || !editedDescription.trim()) return;
    
    setSavingDescription(true);
    try {
      await axios.patch(`${API_URL}/api/codes/${selectedCode.id}/description`, {
        description: editedDescription.trim()
      });
      
      // Update local state
      setSelectedCode(prev => ({ ...prev, description: editedDescription.trim() }));
      setCodes(prev => prev.map(c => 
        c.id === selectedCode.id 
          ? { ...c, description: editedDescription.trim() }
          : c
      ));
      
      setEditingDescription(false);
      toast.success('Descrição atualizada com sucesso!');
      
      // Reload history
      const historyRes = await axios.get(`${API_URL}/api/codes/${selectedCode.id}/history`);
      setHistory(historyRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar descrição');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleSaveUnit = async () => {
    if (!selectedCode || !editedUnit.trim()) return;
    
    setSavingUnit(true);
    try {
      await axios.patch(`${API_URL}/api/codes/${selectedCode.id}/unit`, {
        unit: editedUnit.trim().toUpperCase()
      });
      
      const updatedUnit = editedUnit.trim().toUpperCase();
      
      // Update local state
      setSelectedCode(prev => ({ ...prev, unit: updatedUnit }));
      setCodes(prev => prev.map(c => 
        c.id === selectedCode.id 
          ? { ...c, unit: updatedUnit }
          : c
      ));
      
      setEditingUnit(false);
      toast.success('Unidade atualizada com sucesso!');
      
      // Reload history
      const historyRes = await axios.get(`${API_URL}/api/codes/${selectedCode.id}/history`);
      setHistory(historyRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar unidade');
    } finally {
      setSavingUnit(false);
    }
  };

  const handleCancelEditUnit = () => {
    setEditingUnit(false);
    setEditedUnit(selectedCode?.unit || 'UN');
  };

  const handleCancelEdit = () => {
    setEditingDescription(false);
    setEditedDescription(selectedCode?.description || '');
  };

  const isAdmin = () => user?.role === 'admin';

  const getStatusBadge = (status) => {
    switch (status) {
      case 'inactive':
        return <Badge variant="secondary" className="bg-gray-200 text-gray-600">Inativo</Badge>;
      case 'merged':
        return <Badge variant="secondary" className="bg-purple-100 text-purple-700">Mesclado</Badge>;
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl" data-testid="catalogo-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Catálogo de Códigos
          </h1>
          <p className="text-slate-500 mt-1">
            Busque e visualize todos os códigos cadastrados
          </p>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar por código ou descrição..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 h-10 border-slate-200 focus:border-blue-600"
                  data-testid="search-input"
                />
              </div>
              <Select value={productType} onValueChange={handleProductTypeChange}>
                <SelectTrigger className="w-full sm:w-[200px] h-10 border-slate-200" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" strokeWidth={1.5} />
                  <SelectValue placeholder="Tipo de Produto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {Object.entries(PRODUCT_TYPES).map(([key, value]) => (
                    <SelectItem key={key} value={key}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
                <SelectTrigger className="w-full sm:w-[150px] h-10 border-slate-200" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Ativos</SelectItem>
                  <SelectItem value="inactive">Inativos</SelectItem>
                  <SelectItem value="merged">Mesclados</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                onClick={handleSearch}
                className="bg-slate-900 hover:bg-slate-800 text-white h-10"
                data-testid="search-btn"
              >
                Buscar
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={exporting}
                className="h-10 border-green-200 text-green-700 hover:bg-green-50"
                data-testid="export-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Excel
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className="border-slate-200 shadow-none">
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg font-bold flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                Resultados
                <Badge variant="secondary" className="ml-2 bg-slate-100 text-slate-600">
                  {totalItems.toLocaleString('pt-BR')}
                </Badge>
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Itens por página:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8 border-slate-200" data-testid="page-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0 mt-4">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : codes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" strokeWidth={1.5} />
                <p>Nenhum código encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow className="border-b border-slate-200 hover:bg-transparent">
                      <TableHead className="w-[150px]">Código</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="hidden md:table-cell">Tipo</TableHead>
                      <TableHead className="hidden lg:table-cell">Família</TableHead>
                      <TableHead className="hidden lg:table-cell">Criado por</TableHead>
                      <TableHead className="w-[100px]">Data</TableHead>
                      <TableHead className="w-[80px] text-center">Desenho</TableHead>
                      {statusFilter !== 'active' && <TableHead className="w-[100px]">Status</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow
                        key={code.id}
                        className={`table-row-hover cursor-pointer border-b border-slate-100 ${
                          code.status === 'inactive' ? 'opacity-60' : ''
                        } ${code.status === 'merged' ? 'bg-purple-50/50' : ''}`}
                        onClick={() => handleSelectCode(code)}
                        data-testid={`code-row-${code.id}`}
                      >
                        <TableCell>
                          <code className="material-code text-sm bg-slate-100 px-2 py-1 rounded">
                            {code.code}
                          </code>
                        </TableCell>
                        <TableCell className="max-w-[300px] truncate font-medium text-slate-900">
                          {code.description}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-slate-600">
                          {code.product_type_name}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-slate-600">
                          {code.family_name}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-slate-500 text-sm">
                          {code.created_by_name}
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {new Date(code.created_at).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-center">
                          {code.has_drawing ? (
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPdfViewerCode(code);
                                  setShowPdfViewer(true);
                                }}
                                title="Visualizar PDF"
                                data-testid={`view-pdf-btn-${code.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/desenhos?code=${code.code}`, '_blank');
                                }}
                                title={`${code.drawing_count} desenho(s) - Ver todos`}
                              >
                                <FileBox className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </TableCell>
                        {statusFilter !== 'active' && (
                          <TableCell>{getStatusBadge(code.status)}</TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && codes.length > 0 && (
              <div className="flex items-center justify-between px-4 py-4 border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} de {totalItems.toLocaleString('pt-BR')}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0"
                    data-testid="first-page-btn"
                  >
                    <ChevronsLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="h-8 w-8 p-0"
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center gap-1 mx-2">
                    <span className="text-sm text-slate-600">Página</span>
                    <Input
                      type="number"
                      min={1}
                      max={totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') return; // Allow clearing temporarily
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 1 && num <= totalPages) {
                          setCurrentPage(num);
                        }
                      }}
                      className="w-16 h-8 text-center"
                      data-testid="page-input"
                    />
                    <span className="text-sm text-slate-600">de {totalPages}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0"
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="h-8 w-8 p-0"
                    data-testid="last-page-btn"
                  >
                    <ChevronsRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!selectedCode} onOpenChange={() => { setSelectedCode(null); setShowQRCode(false); setHistory([]); setEditingDescription(false); setEditingUnit(false); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold flex items-center gap-3">
                <code className="material-code bg-slate-100 px-3 py-1 rounded text-lg">
                  {selectedCode?.code}
                </code>
                {selectedCode && getStatusBadge(selectedCode.status)}
              </DialogTitle>
              <DialogDescription>
                Detalhes do código de material
              </DialogDescription>
            </DialogHeader>
            {selectedCode && (
              <div className="space-y-4 mt-4">
                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant={showQRCode ? "outline" : "default"}
                    size="sm"
                    onClick={() => setShowQRCode(false)}
                    className={!showQRCode ? "bg-slate-900" : ""}
                  >
                    <FileText className="w-4 h-4 mr-1" strokeWidth={1.5} />
                    Detalhes
                  </Button>
                  <Button
                    variant={showQRCode ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowQRCode(true)}
                    className={showQRCode ? "bg-slate-900" : ""}
                    data-testid="show-qrcode-btn"
                  >
                    <QrCode className="w-4 h-4 mr-1" strokeWidth={1.5} />
                    QR Code
                  </Button>
                  
                  {/* Deactivate/Activate Button - only for users who can create */}
                  {canCreate() && selectedCode.status !== 'merged' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeactivateDialog(true)}
                      className={selectedCode.status === 'inactive' 
                        ? "border-green-200 text-green-700 hover:bg-green-50" 
                        : "border-orange-200 text-orange-700 hover:bg-orange-50"}
                      data-testid="deactivate-btn"
                    >
                      {selectedCode.status === 'inactive' ? (
                        <>
                          <Power className="w-4 h-4 mr-1" strokeWidth={1.5} />
                          Reativar
                        </>
                      ) : (
                        <>
                          <PowerOff className="w-4 h-4 mr-1" strokeWidth={1.5} />
                          Desativar
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Merge Button - only for active codes */}
                  {canCreate() && selectedCode.status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMergeDialog(true)}
                      className="border-purple-200 text-purple-700 hover:bg-purple-50"
                      data-testid="merge-btn"
                    >
                      <Merge className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      Mesclar
                    </Button>
                  )}
                </div>

                {/* Merged Info */}
                {selectedCode.status === 'merged' && selectedCode.merged_into_code && (
                  <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                    <p className="text-sm text-purple-700">
                      <Merge className="w-4 h-4 inline mr-1" />
                      Este código foi mesclado com <strong>{selectedCode.merged_into_code}</strong>
                    </p>
                  </div>
                )}

                {showQRCode ? (
                  <div className="flex flex-col items-center py-6" data-testid="qrcode-display">
                    <QRCodeSVG
                      value={`TECNOSERVICE|${selectedCode.code}|${selectedCode.description}`}
                      size={200}
                      level="M"
                      includeMargin
                    />
                    <p className="mt-4 text-sm text-slate-500 text-center">
                      Escaneie para identificar o material
                    </p>
                    <code className="mt-2 material-code text-lg">{selectedCode.code}</code>
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center justify-between">
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Descrição
                        </label>
                        {isAdmin() && selectedCode.status !== 'merged' && !editingDescription && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditedDescription(selectedCode.description);
                              setEditingDescription(true);
                            }}
                            className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                            data-testid="edit-description-btn"
                          >
                            <Pencil className="w-3 h-3 mr-1" />
                            Editar
                          </Button>
                        )}
                      </div>
                      {editingDescription ? (
                        <div className="mt-2 space-y-2">
                          <Textarea
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            className="min-h-[80px] text-sm"
                            placeholder="Digite a descrição do item..."
                            data-testid="edit-description-input"
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={handleSaveDescription}
                              disabled={savingDescription || !editedDescription.trim()}
                              className="bg-green-600 hover:bg-green-700 h-8"
                              data-testid="save-description-btn"
                            >
                              {savingDescription ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : (
                                <Check className="w-3 h-3 mr-1" />
                              )}
                              Salvar
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEdit}
                              disabled={savingDescription}
                              className="h-8"
                            >
                              <X className="w-3 h-3 mr-1" />
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-slate-900 font-medium mt-1">
                          {selectedCode.description}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Tipo de Produto
                        </label>
                        <p className="text-slate-900 mt-1">
                          {selectedCode.product_type_name}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Família/Cliente
                        </label>
                        <p className="text-slate-900 mt-1">
                          {selectedCode.family_name}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between">
                          <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                            Unidade
                          </label>
                          {isAdmin() && selectedCode.status !== 'merged' && !editingUnit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditedUnit(selectedCode.unit || 'UN');
                                setEditingUnit(true);
                              }}
                              className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800"
                              data-testid="edit-unit-btn"
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Editar
                            </Button>
                          )}
                        </div>
                        {editingUnit ? (
                          <div className="mt-2 space-y-2">
                            <Input
                              value={editedUnit}
                              onChange={(e) => setEditedUnit(e.target.value.toUpperCase())}
                              className="h-9 text-sm uppercase"
                              placeholder="Ex: UN, KG, M, PC"
                              data-testid="edit-unit-input"
                              maxLength={10}
                            />
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                onClick={handleSaveUnit}
                                disabled={savingUnit || !editedUnit.trim()}
                                className="bg-green-600 hover:bg-green-700 h-8"
                                data-testid="save-unit-btn"
                              >
                                {savingUnit ? (
                                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                ) : (
                                  <Check className="w-3 h-3 mr-1" />
                                )}
                                Salvar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCancelEditUnit}
                                disabled={savingUnit}
                                className="h-8"
                              >
                                <X className="w-3 h-3 mr-1" />
                                Cancelar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-slate-900 mt-1">
                            {selectedCode.unit}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Revisão
                        </label>
                        <p className="text-slate-900 mt-1">
                          {selectedCode.revision?.toString().padStart(2, '0') || '00'}
                        </p>
                      </div>
                    </div>
                    {selectedCode.notes && (
                      <div>
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
                          Observações
                        </label>
                        <p className="text-slate-600 mt-1 whitespace-pre-wrap">
                          {selectedCode.notes}
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-6 pt-4 border-t border-slate-200">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <User className="w-4 h-4" strokeWidth={1.5} />
                        {selectedCode.created_by_name}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        {new Date(selectedCode.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>

                    {/* History */}
                    {history.length > 0 && (
                      <div className="pt-4 border-t border-slate-200">
                        <label className="text-xs uppercase tracking-wider text-slate-500 font-medium flex items-center gap-1">
                          <History className="w-3 h-3" strokeWidth={1.5} />
                          Histórico
                        </label>
                        <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                          {history.slice(0, 5).map((entry, idx) => (
                            <div key={idx} className="flex items-center justify-between text-xs text-slate-500 bg-slate-50 p-2 rounded">
                              <span>
                                {entry.action === 'created' ? 'Criado' : 
                                 entry.action === 'deactivated' ? 'Desativado' :
                                 entry.action === 'activated' ? 'Reativado' :
                                 entry.action === 'merged_into' ? 'Mesclado' :
                                 entry.action === 'merged_from' ? 'Recebeu mesclagem' :
                                 entry.action} por {entry.user_name}
                              </span>
                              <span>{new Date(entry.timestamp).toLocaleDateString('pt-BR')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Deactivate Confirmation Dialog */}
        <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                {selectedCode?.status === 'inactive' ? 'Reativar Código' : 'Desativar Código'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {selectedCode?.status === 'inactive' ? (
                  <>
                    Tem certeza que deseja <strong>reativar</strong> o código <code className="bg-slate-100 px-1 rounded">{selectedCode?.code}</code>?
                    <br /><br />
                    O código voltará a aparecer nas buscas normais.
                  </>
                ) : (
                  <>
                    Tem certeza que deseja <strong>desativar</strong> o código <code className="bg-slate-100 px-1 rounded">{selectedCode?.code}</code>?
                    <br /><br />
                    O código ficará oculto nas buscas normais, mas poderá ser reativado posteriormente.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deactivating}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeactivate}
                disabled={deactivating}
                className={selectedCode?.status === 'inactive' 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-orange-600 hover:bg-orange-700"}
              >
                {deactivating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {selectedCode?.status === 'inactive' ? 'Reativar' : 'Desativar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Merge Dialog */}
        <Dialog open={showMergeDialog} onOpenChange={(open) => {
          setShowMergeDialog(open);
          if (!open) {
            setMergeSearch('');
            setMergeSearchResults([]);
            setSelectedMergeTarget(null);
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Merge className="w-5 h-5 text-purple-600" />
                Mesclar Códigos
              </DialogTitle>
              <DialogDescription>
                Selecione o código para mesclar com <code className="bg-slate-100 px-1 rounded">{selectedCode?.code}</code>.
                O código <strong>mais recente</strong> será mantido ativo.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Buscar código para mesclar
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Digite o código ou descrição..."
                    value={mergeSearch}
                    onChange={(e) => {
                      setMergeSearch(e.target.value);
                      searchMergeCodes(e.target.value);
                    }}
                    className="pl-10"
                    data-testid="merge-search-input"
                  />
                </div>
              </div>

              {/* Search Results */}
              {searchingMerge && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              )}
              
              {!searchingMerge && mergeSearchResults.length > 0 && (
                <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                  {mergeSearchResults.map((code) => (
                    <div
                      key={code.id}
                      className={`p-3 cursor-pointer hover:bg-slate-50 transition-colors ${
                        selectedMergeTarget?.id === code.id ? 'bg-purple-50 border-l-2 border-purple-500' : ''
                      }`}
                      onClick={() => setSelectedMergeTarget(code)}
                      data-testid={`merge-option-${code.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <code className="text-sm bg-slate-100 px-2 py-0.5 rounded">{code.code}</code>
                        <span className="text-xs text-slate-500">
                          {new Date(code.created_at).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 truncate">{code.description}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Target Info */}
              {selectedMergeTarget && (
                <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
                  <p className="text-sm font-medium text-purple-800 mb-2">Código selecionado:</p>
                  <div className="flex items-center justify-between">
                    <code className="bg-purple-100 px-2 py-0.5 rounded text-purple-900">{selectedMergeTarget.code}</code>
                    <span className="text-xs text-purple-600">
                      Criado em {new Date(selectedMergeTarget.created_at).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                  <p className="text-sm text-purple-700 mt-1">{selectedMergeTarget.description}</p>
                  
                  {/* Show which will be kept */}
                  {selectedCode && (
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <p className="text-xs text-purple-600">
                        <strong>Resultado:</strong> O código{' '}
                        <code className="bg-purple-100 px-1 rounded">
                          {new Date(selectedCode.created_at) > new Date(selectedMergeTarget.created_at) 
                            ? selectedCode.code 
                            : selectedMergeTarget.code}
                        </code>{' '}
                        será mantido (mais recente).
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowMergeDialog(false)} disabled={merging}>
                Cancelar
              </Button>
              <Button 
                onClick={handleMerge}
                disabled={!selectedMergeTarget || merging}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="confirm-merge-btn"
              >
                {merging ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Merge className="w-4 h-4 mr-2" />}
                Mesclar Códigos
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Viewer Modal */}
        <PdfViewerModal
          open={showPdfViewer}
          onClose={() => {
            setShowPdfViewer(false);
            setPdfViewerCode(null);
          }}
          code={pdfViewerCode?.code}
          title={pdfViewerCode?.description}
          filename={`${pdfViewerCode?.code}.pdf`}
        />
      </div>
    </Layout>
  );
}
